import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';

/*
 * The check that a sign-up is a person (routes/captcha.py, migration 0107;
 * docs/PUBLIC_PORTAL_AND_ORDERING_PLAN.md 1c). GET /auth/captcha/config says
 * which:
 *
 *   turnstile  Cloudflare's widget, rendered here; its token goes with the
 *              sign-up and the server verifies it (production).
 *   simulated  dev, staging, a developer's machine: a labelled test CAPTCHA
 *              with Pass and Fail, which the server reads as such -- and which
 *              an administrator's test mode can make fail (/admin/test-modes).
 *   off        nothing to show; the sign-up goes ahead without one, and is
 *              never let straight in.
 *
 * `onToken(token | null)` hears every change: a fresh token, or null when
 * there is none (off, a native shell, an expired or failed widget). Without a
 * token a sign-up still goes ahead; it is just not let straight in.
 */
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let configRequest = null;
let scriptRequest = null;

/** GET /auth/captcha/config, asked once. */
export function captchaConfig() {
  if (!configRequest) {
    // Whatever the request does -- throws, answers nothing -- the sign-up
    // still shows, without a CAPTCHA.
    configRequest = Promise.resolve()
      .then(() => api.get('/auth/captcha/config'))
      .then(res => res?.data || { mode: 'off' })
      .catch(() => ({ mode: 'off' }));
  }
  return configRequest;
}

export function resetCaptchaForTests() {
  configRequest = null;
  scriptRequest = null;
}

// A native shell (capacitor://, file://) is not a secure web origin Turnstile
// runs on, as Google's button is not shown there either.
const usableHere = () => typeof window === 'undefined' || window.isSecureContext !== false;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptRequest) {
    scriptRequest = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('no turnstile')));
      script.onerror = () => reject(new Error('Turnstile did not load'));
      document.head.appendChild(script);
    }).catch((err) => { scriptRequest = null; throw err; });
  }
  return scriptRequest;
}

export default function Turnstile({ onToken, action = 'register' }) {
  const { t } = useTranslation('register');
  const [config, setConfig] = useState(null);
  const [simulated, setSimulated] = useState(null);   // 'sim-pass' | 'sim-fail' | null
  const [failed, setFailed] = useState(false);
  const box = useRef(null);
  const latest = useRef(onToken);
  useEffect(() => { latest.current = onToken; });

  useEffect(() => {
    let live = true;
    captchaConfig().then(c => { if (live) setConfig(usableHere() ? c : { mode: 'off' }); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!config) return undefined;
    if (config.mode !== 'turnstile' || !config.site_key) {
      if (config.mode === 'off') latest.current?.(null);
      return undefined;
    }
    let widget = null;
    let live = true;
    loadTurnstile().then((turnstile) => {
      if (!live || !box.current) return;
      widget = turnstile.render(box.current, {
        sitekey: config.site_key,
        action,
        callback: token => latest.current?.(token),
        'expired-callback': () => latest.current?.(null),
        'error-callback': () => { setFailed(true); latest.current?.(null); },
      });
    }).catch(() => { if (live) { setFailed(true); latest.current?.(null); } });
    return () => {
      live = false;
      if (widget !== null && window.turnstile) window.turnstile.remove(widget);
    };
  }, [config, action]);

  if (!config || config.mode === 'off') return null;

  if (config.mode === 'simulated') {
    const choose = (token) => { setSimulated(token); latest.current?.(token); };
    return (
      <fieldset style={styles.box} data-testid="captcha-simulated">
        <legend style={styles.legend}>{t('captcha.testTitle')}</legend>
        <p style={styles.note}>{t('captcha.testNote')}</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => choose('sim-pass')} aria-pressed={simulated === 'sim-pass'}
            title={t('tip.captchaPass')} style={styles.choice(simulated === 'sim-pass')}
            data-testid="captcha-pass">
            {t('captcha.pass')}
          </button>
          <button type="button" onClick={() => choose('sim-fail')} aria-pressed={simulated === 'sim-fail'}
            title={t('tip.captchaFail')} style={styles.choice(simulated === 'sim-fail')}
            data-testid="captcha-fail">
            {t('captcha.fail')}
          </button>
        </div>
      </fieldset>
    );
  }

  return (
    <div style={{ margin: '8px 0 12px' }} data-testid="captcha-turnstile">
      <div ref={box} />
      {failed && <p role="alert" style={styles.note}>{t('captcha.unavailable')}</p>}
    </div>
  );
}

const styles = {
  box: { border: '1px dashed var(--c-border-strong)', borderRadius: 8, padding: '8px 12px', margin: '8px 0 12px' },
  legend: { fontSize: 13, fontWeight: 600, padding: '0 4px' },
  note: { margin: '0 0 8px', fontSize: 12, color: 'var(--c-text-muted-fg)' },
  choice: on => ({
    flex: 1, padding: '6px 10px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
    border: '1px solid var(--c-primary)', background: on ? 'var(--c-primary)' : 'var(--c-surface)',
    color: on ? 'var(--c-on-accent)' : 'var(--c-primary-fg)',
  }),
};
